import { z } from "zod";
import { FIELD_EMAIL_VALID, FIELD_REQUIRED } from "../../i18n/keysTranslations";
import {
  PRE_ACTIVATION_ORGANIZATION_TYPE_AGENCY,
  PRE_ACTIVATION_ORGANIZATION_TYPE_BRAND,
} from "./preActivationFormValues";

const asString = (v) => (v == null ? "" : String(v));
const asStringArray = (v) => (Array.isArray(v) ? v : []);
const asRadioString = (v) => (v == null ? "" : String(v));

const requiredString = (t) =>
  z
    .string()
    .trim()
    .min(1, { message: t(FIELD_REQUIRED) });

const optionalString = () => z.preprocess(asString, z.string());

const requiredRadio = (t) =>
  z.preprocess(
    asRadioString,
    z.string().min(1, { message: t(FIELD_REQUIRED) })
  );

const requiredStringArray = (t) =>
  z.preprocess(
    asStringArray,
    z.array(z.string()).min(1, { message: t(FIELD_REQUIRED) })
  );

const optionalStringArray = () =>
  z.preprocess(asStringArray, z.array(z.string()));

const createSharedSchema = (t) => ({
  organizationType: z.enum([
    PRE_ACTIVATION_ORGANIZATION_TYPE_AGENCY,
    PRE_ACTIVATION_ORGANIZATION_TYPE_BRAND,
  ]),
  fullName: requiredString(t),
  email: requiredString(t).email({ message: t(FIELD_EMAIL_VALID) }),
  businessName: requiredString(t),
  jobTitle: optionalString(),
  whatsApp: optionalString(),
  websiteUrl: optionalString(),
});

const createBrandContactSchema = (t) => ({
  jobTitle: requiredString(t),
  whatsApp: requiredString(t),
});

const createBrandOnlySchema = (t) => ({
  whatYouSell: requiredString(t),
  averagePrice: requiredString(t),
  idealCustomer: requiredString(t),
  whereCustomerBuys: requiredStringArray(t),
  hasAds: requiredRadio(t),
  adPlatforms: optionalStringArray(),
  monthlyAdSpend: requiredString(t),
  currentMetrics: optionalString(),
  targetCpa: requiredString(t),
  bestCampaignMessages: optionalString(),
  monthlySales: requiredString(t),
  salesGoal60to90: requiredString(t),
  buzzlyFocus: requiredRadio(t),
  creativeMaterialLevel: requiredRadio(t),
  creativeMaterialLocation: optionalString(),
  additionalMeetingNotes: optionalString(),
  meetingAccess: requiredStringArray(t),
  accessNotReady: optionalString(),
});

const createAgencyOnlySchema = (t) => ({
  activeBrandsCount: requiredString(t),
  platformUsersCount: requiredString(t),
  agencyAdPlatforms: requiredStringArray(t),
  sessionBrandName: requiredString(t),
  brandAssetsReady: requiredRadio(t),
  creativeBottleneck: requiredString(t),
  usesAdAutomation: requiredRadio(t),
  hasMetaBmAdminAccess: requiredRadio(t),
});

const createOptionalBranchFields = () => ({
  whatYouSell: optionalString(),
  averagePrice: optionalString(),
  idealCustomer: optionalString(),
  whereCustomerBuys: optionalStringArray(),
  hasAds: z.preprocess(asRadioString, z.string()),
  adPlatforms: optionalStringArray(),
  monthlyAdSpend: optionalString(),
  currentMetrics: optionalString(),
  targetCpa: optionalString(),
  bestCampaignMessages: optionalString(),
  monthlySales: optionalString(),
  salesGoal60to90: optionalString(),
  buzzlyFocus: z.preprocess(asRadioString, z.string()),
  creativeMaterialLevel: z.preprocess(asRadioString, z.string()),
  creativeMaterialLocation: optionalString(),
  additionalMeetingNotes: optionalString(),
  meetingAccess: optionalStringArray(),
  accessNotReady: optionalString(),
  activeBrandsCount: optionalString(),
  platformUsersCount: optionalString(),
  agencyAdPlatforms: optionalStringArray(),
  sessionBrandName: optionalString(),
  brandAssetsReady: z.preprocess(asRadioString, z.string()),
  creativeBottleneck: optionalString(),
  usesAdAutomation: z.preprocess(asRadioString, z.string()),
  hasMetaBmAdminAccess: z.preprocess(asRadioString, z.string()),
});

export const createPreActivationSchema = (t) => {
  const brandOnlySchema = createBrandOnlySchema(t);
  const brandContactSchema = createBrandContactSchema(t);
  const agencyOnlySchema = createAgencyOnlySchema(t);

  return z
    .object({
      ...createSharedSchema(t),
      ...createOptionalBranchFields(),
    })
    .superRefine((data, ctx) => {
      if (data.organizationType === PRE_ACTIVATION_ORGANIZATION_TYPE_AGENCY) {
        Object.entries(agencyOnlySchema).forEach(([field, schema]) => {
          const result = schema.safeParse(data[field]);
          if (!result.success) {
            result.error.issues.forEach((issue) => {
              ctx.addIssue({
                ...issue,
                path: [field],
              });
            });
          }
        });
        return;
      }

      Object.entries(brandContactSchema).forEach(([field, schema]) => {
        const result = schema.safeParse(data[field]);
        if (!result.success) {
          result.error.issues.forEach((issue) => {
            ctx.addIssue({
              ...issue,
              path: [field],
            });
          });
        }
      });

      Object.entries(brandOnlySchema).forEach(([field, schema]) => {
        const result = schema.safeParse(data[field]);
        if (!result.success) {
          result.error.issues.forEach((issue) => {
            ctx.addIssue({
              ...issue,
              path: [field],
            });
          });
        }
      });
    });
};
